import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import {
  listAllCompetitions,
  runCompetitionAutoMerge,
  setCompetitionStatus,
  type AdminCompetitionListRow,
} from "../../../lib/admin";
import type { CompetitionStatus } from "../../../types/database";
import { exportRowsAsCsv, fmtCellDate, todayDateForFilename } from "../../../lib/csvExport";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import { supabase } from "../../../lib/supabase";
import {
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
} from "../../../components/admin/ui";
import { StatusPill, type StatusPillTone } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;

// The four "live" states are first-class on this admin screen. The
// approval-flow states (submitted/approved/etc) get their dedicated UI in
// /admin/case-approvals — anything that leaks through here falls back to a
// muted grey pill via STATUS_TONE[s] ?? "grey".
const FILTERS: CompetitionStatus[] = ["draft", "open", "judging", "closed"];
const STATUS_TONE: Partial<Record<CompetitionStatus, StatusPillTone>> = {
  draft: "grey",
  open: "sage",
  judging: "amber",
  closed: "grey",
};

export default function AdminCompetitionsScreen() {
  const [rows, setRows] = useState<AdminCompetitionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const hasLoadedRef = useRef(false);

  const filterUrl = useAdminListFilter({
    keys: ["q", "status", "company", "sort"],
    defaults: { q: "", status: "all", company: "all", sort: "recent" },
  });
  const search = filterUrl.get("q");
  const filter = filterUrl.get("status") as CompetitionStatus | "all";
  const companyFilter = filterUrl.get("company");
  const sortVal = filterUrl.get("sort");

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filterUrl.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      setRows(await listAllCompetitions());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load competitions");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const companyOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      if (r.company_id && r.company_name) map.set(r.company_id, r.company_name);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (companyFilter !== "all" && r.company_id !== companyFilter) return false;
      if (q) {
        const hay = `${r.name} ${r.company_name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sortVal === "closing") {
        const at = a.closes_at ? new Date(a.closes_at).getTime() : Infinity;
        const bt = b.closes_at ? new Date(b.closes_at).getTime() : Infinity;
        return at - bt;
      }
      const order: Record<string, number> = { open: 0, judging: 1, draft: 2, closed: 3 };
      const sa = order[a.status] ?? 99;
      const sb = order[b.status] ?? 99;
      if (sa !== sb) return sa - sb;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [rows, search, filter, companyFilter, sortVal]);

  async function onForceStatus(id: string, status: CompetitionStatus) {
    setBusyId(id);
    try {
      await setCompetitionStatus(id, status);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to set status");
    } finally {
      setBusyId(null);
    }
  }

  async function onAutoMerge(id: string) {
    setBusyId(id);
    try {
      await runCompetitionAutoMerge(id);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to run auto-merge");
    } finally {
      setBusyId(null);
    }
  }

  const COMPANY_CAP = 12;
  const visibleCompanies = showAllCompanies ? companyOptions : companyOptions.slice(0, COMPANY_CAP);
  const hiddenCompanyCount = companyOptions.length - visibleCompanies.length;

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Programs · Competitions"
          title="All competitions"
          subtitle="Force a status change or run auto-merge mid-flight when a competition needs intervention."
          actions={
            <AdminButton
              theme={THEME}
              label="Export CSV"
              variant="outline"
              size="sm"
              onPress={() =>
                exportRowsAsCsv({
                  filename: `competitions_${todayDateForFilename()}.csv`,
                  auditPage: "competitions",
                  columns: [
                    { header: "Name", get: (r: AdminCompetitionListRow) => r.name },
                    { header: "Company", get: (r: AdminCompetitionListRow) => r.company_name ?? "" },
                    { header: "Status", get: (r: AdminCompetitionListRow) => r.status },
                    { header: "Opens", get: (r: AdminCompetitionListRow) => fmtCellDate(r.opens_at) },
                    { header: "Closes", get: (r: AdminCompetitionListRow) => fmtCellDate(r.closes_at) },
                  ],
                  rows: filtered,
                })
              }
            />
          }
        />

        {err ? (
          <View style={{ marginBottom: SPACING.md }}>
            <AdminError theme={THEME} message={err} onRetry={() => void load()} />
          </View>
        ) : null}

        {/* Filter bar */}
        <View
          style={{
            padding: 12,
            borderRadius: RADII.md,
            backgroundColor: THEME.surfaceRaisedBg,
            borderWidth: STROKES.hairline,
            borderColor: THEME.border,
            gap: 10,
          }}
        >
          <AdminInput
            theme={THEME}
            value={searchInput}
            onChangeText={setSearchInput}
            placeholder="Search title or company…"
          />
          <FilterGroup label="Status">
            <FilterChip theme={THEME} label="all" active={filter === "all"} onPress={() => filterUrl.set({ status: "all" })} />
            {FILTERS.map((s) => (
              <FilterChip key={s} theme={THEME} label={s} active={filter === s} onPress={() => filterUrl.set({ status: s })} />
            ))}
          </FilterGroup>
          {companyOptions.length > 0 ? (
            <FilterGroup label="Company">
              <FilterChip theme={THEME} label="All" active={companyFilter === "all"} onPress={() => filterUrl.set({ company: "all" })} />
              {visibleCompanies.map(([id, name]) => (
                <FilterChip key={id} theme={THEME} label={name} active={companyFilter === id} onPress={() => filterUrl.set({ company: id })} />
              ))}
              {companyOptions.length > COMPANY_CAP ? (
                <FilterChip
                  theme={THEME}
                  label={showAllCompanies ? "Show fewer" : `+${hiddenCompanyCount} more`}
                  active={false}
                  onPress={() => setShowAllCompanies((s) => !s)}
                />
              ) : null}
            </FilterGroup>
          ) : null}
          <FilterGroup label="Sort">
            <FilterChip theme={THEME} label="Recent / by status" active={sortVal === "recent"} onPress={() => filterUrl.set({ sort: "recent" })} />
            <FilterChip theme={THEME} label="Closing soon" active={sortVal === "closing"} onPress={() => filterUrl.set({ sort: "closing" })} />
          </FilterGroup>
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading competitions…" />
          ) : filtered.length === 0 ? (
            <AdminEmpty
              theme={THEME}
              title={filter === "all" ? "No competitions yet." : `No ${filter} competitions.`}
            />
          ) : (
            <View style={{ gap: 8 }}>
              {filtered.map((row) => (
                <CompetitionRow
                  key={row.id}
                  row={row}
                  busy={busyId === row.id}
                  onAutoMerge={() => void onAutoMerge(row.id)}
                  onForceStatus={(s) => void onForceStatus(row.id, s)}
                />
              ))}
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>{label}:</Text>
      {children}
    </View>
  );
}

function DisclosureToggle({ label, open, onPress }: { label: string; open: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        { paddingVertical: 4 } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.accent }}>
        {label} {open ? "▴" : "▾"}
      </Text>
    </Pressable>
  );
}

function CompetitionRow({
  row,
  busy,
  onAutoMerge,
  onForceStatus,
}: {
  row: AdminCompetitionListRow;
  busy: boolean;
  onAutoMerge: () => void;
  onForceStatus: (s: CompetitionStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const [positionsOpen, setPositionsOpen] = useState(false);
  return (
    <View
      style={{
        padding: 14,
        borderRadius: RADII.md,
        backgroundColor: THEME.surfaceRaisedBg,
        borderWidth: STROKES.hairline,
        borderColor: THEME.border,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <Pressable
          onPress={() => router.push(`/client/competition/${row.id}` as Href)}
          style={[
            { flex: 1, minWidth: 0 } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 14, color: THEME.textOnSurface }} numberOfLines={1}>
            {row.name}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
            {row.company_name ?? "Company"}
          </Text>
        </Pressable>
        <StatusPill theme={THEME} label={row.status} tone={STATUS_TONE[row.status] ?? "grey"} />
      </View>
      <View style={{ marginTop: 10, flexDirection: "row", gap: 16, flexWrap: "wrap" }}>
        <DisclosureToggle label="Admin actions" open={open} onPress={() => setOpen((s) => !s)} />
        {row.status === "closed" || row.status === "judging" ? (
          <Pressable
            onPress={() => router.push(`/admin/competitions/${row.id}/winners` as Href)}
            style={[
              { paddingVertical: 4 } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.accent }}>
              Review winners →
            </Text>
          </Pressable>
        ) : null}
        <DisclosureToggle label="Positions & opt-ins" open={positionsOpen} onPress={() => setPositionsOpen((s) => !s)} />
      </View>
      {open ? (
        <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {row.status === "open" ? (
            <AdminButton theme={THEME} label="Auto-merge" size="sm" disabled={busy} onPress={onAutoMerge} />
          ) : null}
          {FILTERS.map((s) => (
            <AdminButton
              key={s}
              theme={THEME}
              label={`Force ${s}`}
              size="sm"
              variant="outline"
              disabled={busy || row.status === s}
              onPress={() => onForceStatus(s)}
            />
          ))}
        </View>
      ) : null}
      {positionsOpen ? <PositionsSummary competitionId={row.id} /> : null}
    </View>
  );
}

type AdminPositionRow = {
  position_id: string;
  position_title: string;
  position_status: string;
  active_count: number;
};

function PositionsSummary({ competitionId }: { competitionId: string }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<AdminPositionRow[]>([]);
  const [uniqueOptins, setUniqueOptins] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [summary, unique] = await Promise.all([
          supabase.rpc("admin_competition_position_summary", { p_competition_id: competitionId }),
          supabase.rpc("admin_competition_position_unique_optins", { p_competition_id: competitionId }),
        ]);
        if (!alive) return;
        if (summary.error) throw new Error(summary.error.message);
        if (unique.error) throw new Error(unique.error.message);
        setRows((summary.data ?? []) as AdminPositionRow[]);
        setUniqueOptins(typeof unique.data === "number" ? unique.data : 0);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [competitionId]);

  if (loading) {
    return <AdminLoading theme={THEME} />;
  }
  if (err) {
    return (
      <Text style={{ marginTop: 10, fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.destructive }}>
        {err}
      </Text>
    );
  }
  if (rows.length === 0) {
    return (
      <Text style={{ marginTop: 10, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceFaint }}>
        No positions linked to this competition yet.
      </Text>
    );
  }

  return (
    <View
      style={{
        marginTop: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: RADII.sm,
        backgroundColor: THEME.altBg,
        borderWidth: STROKES.hairline,
        borderColor: THEME.border,
      }}
    >
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
        {rows.length} position{rows.length === 1 ? "" : "s"} · {uniqueOptins} unique student
        {uniqueOptins === 1 ? "" : "s"} opted in
      </Text>
      <View style={{ marginTop: 8, gap: 4 }}>
        {rows.map((r) => (
          <View key={r.position_id} style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
              {r.position_title}
            </Text>
            <Text style={{ fontFamily: THEME.bodyFont, fontSize: 10.5, color: THEME.textOnSurfaceFaint }}>
              {r.position_status}
            </Text>
            <Text style={{ width: 70, textAlign: "right", fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.accent }}>
              {r.active_count} opted in
            </Text>
          </View>
        ))}
      </View>
      <Text style={{ marginTop: 8, fontFamily: THEME.bodyFont, fontSize: 10.5, color: THEME.textOnSurfaceFaint }}>
        Read-only — clients own positions. The student identities are gated until judging closes.
      </Text>
    </View>
  );
}
